import { Component, Input, OnInit, Inject, Output, EventEmitter } from '@angular/core';
import { BaseEnrollmentComponent } from '../base-enrollment/base-enrollment.component';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DialogService } from 'src/app/services/dialog.service';
import { EntityRESTService } from 'src/app/services/entity-rest.service';
import { UtilityService } from 'src/app/services/utility.service';
import { FieldsService } from 'src/app/services/fields.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { TaxonomyService } from 'src/app/services/taxonomy.service';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthRESTService } from 'src/app/services/auth-rest.service';
import { AuthService } from 'src/app/services/auth.service';
import { MenuRESTService } from 'src/app/services/menu-rest.service';
import { RouteEventsService } from 'src/app/route-events.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { Renderer2, ElementRef, ChangeDetectorRef } from '@angular/core';
import { ConfigPagesService } from 'src/app/services/config-pages.service';
import { PackagesService } from 'src/app/services/packages.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserService } from 'src/app/services/user.service';
import { DomSanitizer } from '@angular/platform-browser';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { RefreshService } from 'src/app/services/refresh.service';
import { StudioConfigService } from 'src/app/services/studio-config.service';
import { ComponentRefreshService } from 'src/app/services/component-refresh.service';
import { TranslateService } from '@ngx-translate/core';
import { PhoneNumberService } from 'src/app/services/phone-number.service';
import { agreementTaxLabel } from '../agreement-tax-label';
import { agreementPackageName } from '../agreement-package-name';

interface EnrollmentTypeState {
  original: boolean;
  extension: boolean;
  renewal: boolean;
  service: boolean;
}

@Component({
  selector: 'app-enrollment-agreement-display',
  templateUrl: './enrollment-agreement-display.component.html',
  styleUrls: ['./enrollment-agreement-display.component.css']
})

export class EnrollmentAgreementDisplayComponent extends BaseEnrollmentComponent implements OnInit {
  @Input() override entityData: any;
  @Input() override studentAccountEntityData: any;
  @Input() override studioInformationConfig: any;
  @Input() override enrollmentForm: any;
  @Input() displaySignatures = true;
  @Input() override _enrollmentEntityData: any;
  @Input() displayPrintButton = true;
  @Input() displayEmailButton = true;
  @Input() displaySigned = true;
  @Input() displayBottomStudioInfo = true;
  @Input() override isTaxInclusive = false;
  @Input() override taxConfig: any[] = [];
  @Input() templateKey: string = 'amdc_standard';

  /**
   * Resolve the effective template key:
   * 1. If entityData has an expanded enrollment_agreement with field_template_key, use it (what they signed)
   * 2. Otherwise fall back to the templateKey input (set by parent from studio default)
   */
  get effectiveTemplateKey(): string {
    const agreement = this.entityData?.field_enrollment_agreement;
    if (agreement && typeof agreement === 'object' && agreement.field_template_key) {
      return agreement.field_template_key;
    }
    // If entityData exists but has no agreement ref, and agreement field is empty string (legacy), use US Standard
    if (this.entityData && this.entityData.field_enrollment_agreement === '') {
      return 'amdc_standard';
    }
    return this.templateKey;
  }

  get isTemplateReady(): boolean {
    return !!this.entityData;
  }

  /**
   * Unwrap a Drupal date field into a value the DatePipe can render.
   * Drupal returns dates as the array shape `[{ value: '...' }]`, but the mock
   * create-flow entity and post-save reloads can also hand back a scalar or a
   * `{ value }` object. The DatePipe returns null silently for null/undefined,
   * but throws NG02100 (InvalidPipeArgument) on '' or on a raw object/array, so
   * coerce empty/non-date values to null instead of letting the array fall through.
   */
  private unwrapDate(field: any): any {
    const value = Array.isArray(field) ? field[0]?.value : (field?.value ?? field);
    return value ? value : null;
  }

  get expirationDate(): any {
    // Prefer the live form value so the date shown while signing matches what
    // was just entered on the form; the datepicker hands back a Moment, which
    // the DatePipe can't render. Fall back to the saved entity.
    const formVal = this.enrollmentForm?.get?.('field_expiration_date')?.value;
    if (formVal !== undefined && formVal !== null && formVal !== '') {
      return (typeof formVal === 'object' && typeof formVal.toDate === 'function')
        ? formVal.toDate()
        : formVal;
    }
    return this.unwrapDate(this.entityData?.['field_expiration_date']);
  }

  get saleDate(): any {
    return this.unwrapDate(this.entityData?.['field_sale_date']);
  }

  @Output() printRequested = new EventEmitter<{ showBackOnly: boolean; showFrontOnly: boolean; }>();

  constructor(
    public override _dialogRef: MatDialogRef<unknown>,
    public override _dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public override _injectedDialogData: any,
    public override _refreshService: RefreshService,
    public override _dialogService: DialogService,
    public override _entityRESTService: EntityRESTService,
    public override _utilityService: UtilityService,
    public override _fieldsService: FieldsService,
    public override _drupalRESTService: DrupalRESTService,
    public override _taxonomyService: TaxonomyService,
    public override _router: Router,
    public override _activatedRoute: ActivatedRoute,
    public override _authRESTService: AuthRESTService,
    public override _authService: AuthService,
    public override _menuRESTService: MenuRESTService,
    public override _formBuilder: FormBuilder,
    public override _routeEventsService: RouteEventsService,
    public override _permissionsService: PermissionsService,
    public override _renderer2: Renderer2,
    public override _configPages: ConfigPagesService,
    public override _packagesService: PackagesService,
    public override _snackBar: MatSnackBar,
    public override _userRESTService: UserService,
    public override _el: ElementRef,
    public override _changeDetectorRef: ChangeDetectorRef,
    public override _sanitizer: DomSanitizer,
    public override _studioConfigService: StudioConfigService,
    public override _componentRefreshService: ComponentRefreshService,
    public override _translateService: TranslateService,
    phoneNumberService: PhoneNumberService
  ) {
    super(
      _dialogRef,
      _dialog,
      _injectedDialogData,
      _refreshService,
      _dialogService,
      _entityRESTService,
      _utilityService,
      _fieldsService,
      _drupalRESTService,
      _taxonomyService,
      _router,
      _activatedRoute,
      _authRESTService,
      _authService,
      _menuRESTService,
      _formBuilder,
      _routeEventsService,
      _permissionsService,
      _renderer2,
      _configPages,
      _packagesService,
      _snackBar,
      _userRESTService,
      _el,
      _changeDetectorRef,
      _sanitizer,
      _studioConfigService,
      _componentRefreshService,
      _translateService,
      phoneNumberService
    );
  }

  /**
   * Calculate the original package cost
   */
  getOriginalPackageCost(): number {
    if (this.entityData?.field_enrollment_lesson_price && this.entityData?.field_enrollment_lesson_count) {
      return this.entityData.field_enrollment_lesson_price * this.entityData.field_enrollment_lesson_count;
    }
    return 0;
  }

  getNotes(): string {
    const formVal = this.enrollmentForm?.get('field_notes')?.value;
    if (typeof formVal === 'string' && formVal) return formVal;
    const raw = this.entityData?.['field_notes'];
    if (typeof raw === 'string') return raw;
    if (Array.isArray(raw) && typeof raw[0]?.value === 'string') return raw[0].value;
    if (raw && typeof raw === 'object' && typeof raw.value === 'string') return raw.value;
    return '';
  }

  /**
   * Calculate the total tuition
   */
  calculateTotalTuition(): number {
    if (this.entityData?.field_enrollment_total_price) {
      return this.entityData.field_enrollment_total_price;
    }
    return 0;
  }

  /**
   * Emits an event requesting the parent component to print.
   * @param showBackOnly Whether to show only the back page.
   * @param showFrontOnly Whether to show only the front page.
   */
  requestPrint(showBackOnly: boolean, showFrontOnly: boolean): void {
    this.printRequested.emit({ showBackOnly, showFrontOnly });
  }

  areAllPaymentAmountsSame(): boolean {
    const payments = this.entityData?.field_scheduled_payments;

    if (!payments) {
      return true; // No payments data
    }

    // Handle case where payments is an object with numeric keys (like {'0': {...}})
    if (!Array.isArray(payments)) {
      // Convert object to array if it has numeric keys
      const paymentKeys = Object.keys(payments).filter(key => !isNaN(Number(key)));
      if (paymentKeys.length <= 1) {
        return true; // Only one or no payments
      }

      // Get the first payment amount
      const firstAmount = this.getPaymentAmount(payments[paymentKeys[0]]);

      // Check if all other payments have the same amount
      return paymentKeys.every(key => {
        const amount = this.getPaymentAmount(payments[key]);
        return Math.abs(amount - firstAmount) < 0.01; // Use small tolerance for floating point comparison
      });
    }

    // Handle array case
    if (payments.length <= 1) {
      return true; // If there's only one or no payments, consider it "same"
    }

    // Get the first payment amount
    const firstAmount = this.getPaymentAmount(payments[0]);

    // Check if all other payments have the same amount
    return payments.every(payment => {
      const amount = this.getPaymentAmount(payment);
      return Math.abs(amount - firstAmount) < 0.01; // Use small tolerance for floating point comparison
    });
  }

  private getPaymentAmount(payment: any): number {
    // Handle different data structures for payment amount
    if (payment?.field_payment_amount) {
      return parseFloat(payment.field_payment_amount) || 0;
    } else if (payment?.['0']?.field_payment_amount) {
      return parseFloat(payment['0'].field_payment_amount) || 0;
    }
    return 0;
  }

  getFirstPaymentAmount(): number {
    const payments = this.entityData?.field_scheduled_payments;
    if (!payments) {
      return 0;
    }

    // Handle object structure
    if (!Array.isArray(payments)) {
      const paymentKeys = Object.keys(payments).filter(key => !isNaN(Number(key)));
      if (paymentKeys.length === 0) {
        return 0;
      }
      return this.getPaymentAmount(payments[paymentKeys[0]]);
    }

    // Handle array structure
    if (payments.length === 0) {
      return 0;
    }
    return this.getPaymentAmount(payments[0]);
  }

  /**
   * The cash down payment for the lending disclosure. Sourced from the scheduled
   * payment flagged as the down payment (field_down_payment), which is how the
   * installment generator records it. Falls back to the enrollment-level
   * field_payment_amount (used by paid-in-full enrollments).
   */
  getDownPaymentAmount(): number {
    const payments = this.entityData?.field_scheduled_payments;

    const isDownPayment = (p: any): boolean => {
      const v = p?.field_down_payment ?? p?.['0']?.field_down_payment;
      return v === true || v === 1 || v === '1';
    };

    if (payments) {
      const list = Array.isArray(payments)
        ? payments
        : Object.keys(payments).filter(k => !isNaN(Number(k))).map(k => payments[k]);

      const flagged = list.find(isDownPayment);
      if (flagged) {
        return this.getPaymentAmount(flagged);
      }
    }

    return parseFloat(this.entityData?.field_payment_amount) || 0;
  }

  getFirstPaymentDate(): string {
    const payments = this.entityData?.field_scheduled_payments;
    if (!payments) {
      return 'Not specified';
    }

    let firstPayment;

    // Handle object structure
    if (!Array.isArray(payments)) {
      const paymentKeys = Object.keys(payments).filter(key => !isNaN(Number(key)));
      if (paymentKeys.length === 0) {
        return 'Not specified';
      }
      firstPayment = payments[paymentKeys[0]];
    } else {
      // Handle array structure
      if (payments.length === 0) {
        return 'Not specified';
      }
      firstPayment = payments[0];
    }

    if (firstPayment?.field_payment_date) {
      return firstPayment.field_payment_date;
    } else if (firstPayment?.['0']?.field_payment_date) {
      return firstPayment['0'].field_payment_date;
    }

    // Fall back to field_next_scheduled_payment if available
    return this.entityData?.field_next_scheduled_payment || 'Not specified';
  }

  getCategoryTargetId(): string | null {
    const categoryField = this.entityData?.field_category;
    if (!categoryField) {
      return null;
    }

    if (Array.isArray(categoryField)) {
      return categoryField[0]?.target_id?.toString() ?? null;
    }

    if (typeof categoryField === 'object') {
      return categoryField?.target_id?.toString() ?? null;
    }

    return categoryField?.toString() ?? null;
  }

  isCategory(expected: string | number): boolean {
    const targetId = this.getCategoryTargetId();
    if (!targetId) {
      return false;
    }
    return targetId === expected.toString();
  }

  get enrollmentTypeState(): EnrollmentTypeState {
    const category = this.getCategoryTargetId();

    // Map each category to exactly ONE type (priority: Original > Extension > Renewal > Service)
    // Based on DATA_STRUCTURE.txt enrollment_type taxonomy
    const categoryMap: { [key: string]: 'original' | 'extension' | 'renewal' | 'service' } = {
      '50': 'original',      // Original
      '49': 'original',      // Pre-Original
      '51': 'extension',     // Extension
      '543': 'extension',    // DOR (Dance of Romance)
      '546': 'extension',    // Party Time
      '65': 'renewal',       // Renewals
      '5': 'service',        // Miscellaneous
      '66': 'service',       // Non-Unit
      '67': 'service',       // Sundry
      '68': 'service',       // Transfer In
      '69': 'service',       // Bonus/Comp Lesson
      '542': 'service',      // Showcase/Medal Ball
      '1373': 'service',     // Default
      '1395': 'service',     // DE
      '1421': 'original',    // History (treat as original)
      '70': 'original'       // Uncategorized (default to original)
    };

    const mappedType = category ? categoryMap[category] : null;

    // Return state with only one checkbox true
    return {
      original: mappedType === 'original',
      extension: mappedType === 'extension',
      renewal: mappedType === 'renewal',
      service: mappedType === 'service' || mappedType === null // Default to service if unknown
    };
  }

  private normalizeBoolean(value: unknown): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }

    // Handle array or Drupal field object shapes
    if (Array.isArray(value)) {
      return this.normalizeBoolean(value[0]?.value ?? value[0]);
    }

    if (typeof value === 'object') {
      // Common Drupal shapes: { value: '0' } or { 0: { value: '0' } }
      const objectValue = (value as any)?.value ?? (value as any)?.[0]?.value ?? (value as any)?.[0];
      if (objectValue !== undefined) {
        return this.normalizeBoolean(objectValue);
      }
    }

    // Normalize string values
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['1', 'true', 'yes'].includes(normalized)) {
        return true;
      }
      if (['0', 'false', 'no'].includes(normalized)) {
        return false;
      }
    }

    return value === true || value === 1;
  }

  get earlyPaymentPenalty(): boolean {
    return this.normalizeBoolean(this.entityData?.field_early_payment_penalty);
  }

  get earlyPaymentRefund(): boolean {
    // Default to true; only explicit false/'false' disable refund
    const value = this.entityData?.field_early_payment_refund;
    if (value === false || value === 'false') {
      return false;
    }
    return true;
  }

  getTaxLabel(): string {
    return agreementTaxLabel(this.taxConfig, 'Sales Tax');
  }

  /**
   * Package title for the agreement. The signing view builds entityData from
   * the form, where the autocomplete stores "Title (id)", while the saved
   * entity collapses the reference to an object. agreementPackageName
   * normalizes both and blanks out bare ids.
   */
  getPackageName(): string {
    const formVal = this.enrollmentForm?.get?.('field_enrollment_package_name')?.value;
    return agreementPackageName(formVal) ||
      agreementPackageName(this.entityData?.['field_enrollment_package_name']);
  }

  /**
   * Override ngAfterContentInit to prevent calling getEntity
   * This component receives data via @Input properties, not by fetching
   */
  override ngAfterContentInit() {
    // Don't call super.ngAfterContentInit() which would trigger getEntity
    // This component gets its data via @Input properties
  }

  /**
   * Returns the primary contact from studentAccountEntityData.
   * Handles both single object and array formats for field_contacts.
   * When there are multiple contacts, returns the first non-archived one.
   */
  get primaryContact(): any {
    const contacts = this.studentAccountEntityData?.field_contacts;
    if (!contacts) {
      return null;
    }
    // If it's an array, filter out archived contacts and return the first active one
    if (Array.isArray(contacts)) {
      const activeContacts = contacts.filter(
        contact => contact.field_archived !== '1' && contact.field_archived !== 1
      );
      // Return first active contact, or fall back to first contact if all are archived
      return activeContacts[0] || contacts[0] || null;
    }
    // Otherwise, it's already a single object
    return contacts;
  }
}

